import { api, setAccessToken } from './client';

/**
 * Sign-in, against `/api/v1/auth`.
 *
 * Every call here is `anonymous: true` except `me`. Presenting a stale token
 * to the endpoints that exist to replace it would trip the client's 401
 * handler and sign the customer out in the middle of signing them in.
 */

/**
 * Who lives at the address - a household, or one person sharing it.
 *
 * Asked because it changes what a job actually is: a family booking a deep
 * clean and a bachelor booking one are different visits, in size, in timing
 * and in who is home. Null until answered; never inferred.
 */
export type HouseholdType = 'family' | 'bachelor';

/**
 * Only meaningful alongside `householdType: 'bachelor'` - see the screen.
 *
 * `prefer_not_to_say` is a REAL stored value, not the absence of an answer.
 * Someone who declines has answered, and the difference matters: null means
 * nobody asked yet, and an app that treats a decline as unasked will keep
 * asking.
 */
export type Gender = 'male' | 'female' | 'other' | 'prefer_not_to_say';

export type AuthUser = {
  id: string;
  phone: string;
  name: string | null;
  email: string | null;
  photoUrl: string | null;
  /**
   * The two profile answers, both nullable and both optional.
   *
   * The backend returns them now. They stay OPTIONAL on the type so an
   * app built against an older server still compiles and behaves - absent and
   * null are read identically as "not answered", which is what the screen
   * draws as no selection.
   */
  householdType?: HouseholdType | null;
  gender?: Gender | null;
  /**
   * Set when this account has been closed at the account holder's request.
   *
   * **Signing in on a closed account succeeds** - the number is never retired,
   * so the OTP reaches the same account - and this field is the only thing
   * that says so. Without reading it the app would sign somebody into an
   * account whose every other request answers 403, with nothing on screen to
   * explain why the home page is empty.
   *
   * Optional for the same reason as the two above: an app built against a
   * server that predates the field still compiles, and absent reads as "not
   * deleted", which is what that server means.
   */
  deletedAt?: string | null;
};

/**
 * `AuthUser.phone` as `+91 98765 43210`.
 *
 * The backend's `customers` table stores E.164 (`+919876543210`), which is
 * what this now receives - but every screen that prints a phone number was
 * written against the OTP sheet's bare ten digits and prepended `+91` itself,
 * so a real session doubled up: `+91 +919876543210`. Stripping any `+91`/`91`
 * prefix before re-adding it makes the one function correct for both shapes,
 * rather than trusting the caller to already know which one it has.
 */
export const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').replace(/^91(?=\d{10}$)/, '');
  return digits.length === 10
    ? `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`
    : `+91 ${digits}`;
};

export type OtpRequested = {
  /** Identifies this OTP attempt to the server - required back on verify. */
  providerRef: string;
  /** How long the app must disable Resend for. Not sent today; AuthSheet falls back to a local constant. */
  resendInSeconds?: number;
  /**
   * The code itself, in development only.
   *
   * The backend includes this when no SMS provider is configured, so the app
   * can be used without one. It is absent in production and the app must never
   * depend on it - `AuthSheet` shows it as a dev hint and nothing reads it.
   */
  devCode?: string;
};

export type Session = {
  accessToken: string;
  refreshToken: string;
  /** True the first time this number has ever verified. */
  isNewUser: boolean;
  user: AuthUser;
};

/** Step one: ask for a code. Creates no account - see the backend's OtpRequest. */
export const requestOtp = (phone: string) =>
  api.post<OtpRequested>(
    '/auth/otp/request',
    { phone, actorType: 'customer' },
    { anonymous: true },
  );

/**
 * Step two: exchange the code for tokens.
 *
 * `providerRef` is the one `requestOtp` returned - the server ties the code
 * to that specific request, not just to the phone number, so a code typed
 * against a stale ref (an old sheet-open, a previous resend) is rejected
 * even if the digits are right.
 *
 * The access token is pushed into the client here rather than left to the
 * caller. Every request after this one needs it, and a caller that forgets
 * produces a signed-in app whose every screen 401s.
 */
export const verifyOtp = async (
  phone: string,
  code: string,
  providerRef: string,
): Promise<Session> => {
  const session = await api.post<Session>(
    '/auth/otp/verify',
    { phone, code, providerRef, actorType: 'customer' },
    { anonymous: true },
  );

  setAccessToken(session.accessToken);
  return session;
};

/** Trade a refresh token for a fresh pair. */
export const refresh = async (refreshToken: string) => {
  const tokens = await api.post<{ accessToken: string; refreshToken: string }>(
    '/auth/refresh',
    { refreshToken },
    { anonymous: true },
  );

  setAccessToken(tokens.accessToken);
  return tokens;
};

/**
 * Who the current token belongs to.
 *
 * What the app calls on launch to find out whether a stored token is still
 * worth anything. A 401 here is the signal to sign out - which the client
 * raises on its own, so the caller only has to handle the throw.
 */
export const me = () => api.get<AuthUser>('/auth/me');

export type ProfileUpdate = {
  /** Omit to leave the name unchanged. An empty string clears it. */
  name?: string;
  /** Omit to leave the email unchanged. An empty string clears it. */
  email?: string;
  /**
   * Omit to leave unchanged; `null` clears the answer back to unasked.
   *
   * -------------------------------------------------------------------------
   * THE SAVE THIS USED TO BREAK
   * -------------------------------------------------------------------------
   * This note used to say the server did not accept them, and that sending
   * them only when answered kept a save from failing "on an endpoint that has
   * not shipped the fields". That did not work. The backend validates with
   * `forbidNonWhitelisted`, so the first customer to pick a household type got
   * "property householdType should not exist" - and lost their NAME and EMAIL
   * edits along with it. A field the server has never heard of does not
   * degrade quietly; it fails the whole request.
   *
   * `PATCH /auth/me` accepts both now, and answers with what it stored.
   */
  householdType?: HouseholdType | null;
  /** Sent only with `householdType: 'bachelor'` - see the screen's note. */
  gender?: Gender | null;
};

/**
 * Save an edited name and/or email against the signed-in account.
 *
 * The phone number is not editable here - see the backend's note on
 * `UpdateProfileDto` - so `EditProfileSheet` never sends one.
 */
export const updateProfile = (updates: ProfileUpdate) =>
  api.patch<AuthUser>('/auth/me', updates);

/**
 * Ask for the signed-in account to be deleted.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * App Store guideline 5.1.1(v): an app that lets people create an account must
 * let them delete it from inside the app. Offering only deactivation, or only
 * a support email, is explicitly not enough. Version 1.0 (3) was rejected for
 * this - the row existed on the privacy page and did nothing.
 *
 * 5.1.1(v) asks that deletion be INITIATED in the app, not that it finish in
 * the same round trip, which is what makes the queue behind this acceptable.
 *
 * ---------------------------------------------------------------------------
 * IT RECORDS A REQUEST. IT DELETES NOTHING
 * ---------------------------------------------------------------------------
 * Our team acts on it, or it goes through on its own after ninety days. The
 * account keeps working until then - bookings still run, a professional still
 * arrives - so **`HomeScreen` must not sign anybody out on success**, and no
 * longer does.
 */
export const requestAccountDeletion = () => api.delete<void>('/auth/me');

/**
 * Bring a closed account back.
 *
 * The other half, and the reason a closed account can still sign in: the
 * number is never retired, so the OTP reaches the same account, `/auth/me`
 * answers with `deletedAt` set, and this is what the Restore button on that
 * screen calls. `204`, no body.
 *
 * Nothing is rebuilt - the account was closed, not emptied, so the bookings,
 * the addresses and the history are all where they were.
 */
export const restoreAccount = () => api.post<void>('/auth/me/restore');
